import { z } from 'zod';
import { ErrorResponseSchema } from '@/schemas/common/base';
import { idValidator } from '@/schemas/common/validators';

/**
 * Confidence level for price suggestions
 */
export const PriceConfidenceSchema = z.enum(['high', 'medium', 'low']);

/**
 * Request schema for POST /api/ai/suggest-price
 */
export const SuggestPriceRequestSchema = z.object({
  /** The listing ID to analyze for price suggestion */
  listingId: idValidator,
});

/**
 * Price suggestion data from AI analysis
 */
export const PriceSuggestionSchema = z.object({
  /** Suggested price in dollars */
  suggestedPrice: z.number().nonnegative(),
  /** Low-end price estimate based on condition */
  priceLow: z.number().nonnegative(),
  /** High-end price estimate based on condition */
  priceHigh: z.number().nonnegative(),
  /** Confidence level of the suggestion */
  confidence: PriceConfidenceSchema,
  /** Short reason why this is the price floor (max 10 words) */
  priceLowReason: z.string().max(80).optional(),
  /** Short reason why this is the price ceiling (max 10 words) */
  priceHighReason: z.string().max(80).optional(),
  /** Estimated width in inches */
  width: z.number().nonnegative().optional(),
  /** Estimated height in inches */
  height: z.number().nonnegative().optional(),
  /** Estimated depth in inches */
  depth: z.number().nonnegative().optional(),
  /** Estimated weight in pounds */
  weight: z.number().nonnegative().optional(),
});

/**
 * The part of a suggestion a listing form sends back with the listing, so it
 * can be kept as a signal (utils/price-suggestion-signal.ts).
 */
export const SentSuggestionSchema = PriceSuggestionSchema.pick({
  suggestedPrice: true, priceLow: true, priceHigh: true, confidence: true, priceLowReason: true, priceHighReason: true,
});

/**
 * Success response for price suggestion
 */
export const SuggestPriceSuccessSchema = z.object({
  success: z.literal(true),
  suggestion: PriceSuggestionSchema,
});

/**
 * Response schema for POST /api/ai/suggest-price
 */
export const SuggestPriceResponseSchema = z.discriminatedUnion('success', [
  SuggestPriceSuccessSchema,
  ErrorResponseSchema,
]);

/**
 * Listing data used for price suggestions (from Prisma supplyListings table)
 */
export const SuggestPriceListingDataSchema = z.object({
  id: z.number().int().positive(),
  userId: z.number().int().positive(),
  title: z.string(),
  text: z.string().nullable(),
  price: z.number().nonnegative().nullable(),
  categoryId: z.number().int().positive().nullable(),
  hasPhoto: z.number().int().nonnegative(),
  dataSourceId: z.number().int().positive().nullable(),
});

/**
 * Category data used for price suggestions (from Prisma categories table)
 */
export const SuggestPriceCategorySchema = z.object({
  name: z.string(),
});

/**
 * Listing metadata with photo URLs for price suggestions (from Prisma listingsMetadata table)
 */
export const SuggestPriceMetadataSchema = z.object({
  photo0: z.string().nullable(),
  photo1: z.string().nullable(),
  photo2: z.string().nullable(),
  photo3: z.string().nullable(),
  photo4: z.string().nullable(),
  photo5: z.string().nullable(),
  photo6: z.string().nullable(),
  photo7: z.string().nullable(),
});

/**
 * Inline image for draft price suggestions (base64-encoded)
 */
export const SuggestPriceInlineImageSchema = z.object({
  base64: z.string().min(1),
  mediaType: z.string().min(1),
});

/**
 * Request schema for POST /api/ai/suggest-price-draft
 * Accepts inline data for unsaved listings (e.g. /sell page)
 * Title is optional — if blank, AI identifies the item first
 */
export const SuggestPriceDraftRequestSchema = z.object({
  title: z.string().optional().default(''),
  description: z.string().optional().default(''),
  category: z.string().optional().default(''),
  condition: z.string().optional().default(''),
  images: z.array(SuggestPriceInlineImageSchema).min(1, 'At least one image is required').max(4),
});

/**
 * Full AI analysis response — includes identification + pricing
 */
export const AiFullAnalysisSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  categoryId: z.number().int().positive().optional(),
  condition: z.enum(['Brand New', 'Like New', 'Good', 'Gently Used', 'Quite Used']).optional(),
  width: z.number().nonnegative().optional(),
  height: z.number().nonnegative().optional(),
  depth: z.number().nonnegative().optional(),
  weight: z.number().nonnegative().optional(),
  suggestedPrice: z.number().nonnegative(),
  priceLow: z.number().nonnegative(),
  priceHigh: z.number().nonnegative(),
  confidence: PriceConfidenceSchema,
  priceLowReason: z.string().max(80).optional(),
  priceHighReason: z.string().max(80).optional(),
});

/**
 * Stored AI price analysis shape (persisted as JSON in supplyListings.aiPriceAnalysis)
 */
export const AiPriceAnalysisSchema = z.object({
  suggestedPrice: z.number().nonnegative(),
  priceLow: z.number().nonnegative(),
  priceHigh: z.number().nonnegative(),
  confidence: PriceConfidenceSchema,
  priceLowReason: z.string().max(80).optional(),
  priceHighReason: z.string().max(80).optional(),
  /**
   * What comparable items ACTUALLY sell for on the secondary market, as
   * distinct from suggestedPrice, which is what this particular listing ought
   * to be priced at. The deal score (utils/deal-score.ts) measures the asking
   * price against this when it is present. Written by the tag-time analyser;
   * absent on rows the older suggest-price endpoint wrote.
   */
  marketPrice: z.number().nonnegative().optional(),
  /** Where the comp came from, in a few words. Shown in the dash, never to buyers. */
  marketBasis: z.string().max(120).optional(),
  /**
   * Which pass produced this, best first: `seller` is the seller's own run of
   * the price tool, `estimate` is the dedicated market-estimate pass, `text` is
   * the free one that rides on the tagging call. A better source may replace a
   * worse one and never the reverse -- without it the free tagger estimate
   * would win the race against the dedicated pass moments later. Absent means
   * `seller`, because that endpoint was the only writer before this field
   * existed.
   *
   * `comps` is the median of the comparable listings Jev accepted
   * (utils/market-estimate.ts, from 2026-10-02); it ranks with `estimate`.
   *
   * `photos` is historical: it names the rows written while the estimator sent
   * images. Those rows are as good as the ones written now, so it is read at
   * the same rank as `estimate` and never written. See utils/market-estimate.ts.
   *
   * `suggested` is the sell page's AI suggestion, kept because the seller
   * priced the listing at it (utils/price-suggestion-signal.ts, 2026-10-10).
   * It ranks with `text`, below comparables: the reference a listing has until
   * Jev finds comparables, not one that stops it looking.
   *
   * `neighbors` is the median price of the listings nearest by meaning with no
   * judge (scripts/estimate-deals-by-meaning.ts --no-judge, 2026-10-10): always
   * `medium` confidence, ranked with `text` so Jev's comparables replace it.
   *
   * `shopping` is the median of resale offers (eBay, Poshmark, Etsy...) for
   * the same or a similar item on Google Shopping (utils/shopping-price.ts,
   * 2026-10-10): real asking prices, no model. It ranks with `comps` and
   * replaces the text-era `estimate` and a low-confidence `comps`.
   */
  estimateSource: z.enum(['seller', 'comps', 'estimate', 'photos', 'text', 'suggested', 'neighbors', 'shopping', 'fallback']).optional(),
  /**
   * Which model produced it. Recorded because models disagree SYSTEMATICALLY,
   * not just noisily: measured on live listings, Opus 4.6 sits 21% below Opus 5
   * and Sonnet 5 23% below it. A deal score is relative, so a corpus estimated
   * half by one model and half by another is a corpus whose scores are not
   * comparable -- and without this field there is no way to find the half that
   * needs redoing.
   */
  estimateModel: z.string().max(40).optional(),
  analyzedAt: z.string(),
});

// Type exports
export type PriceConfidence = z.infer<typeof PriceConfidenceSchema>;
export type SuggestPriceRequest = z.infer<typeof SuggestPriceRequestSchema>;
export type SuggestPriceDraftRequest = z.infer<typeof SuggestPriceDraftRequestSchema>;
export type PriceSuggestion = z.infer<typeof PriceSuggestionSchema>;
export type SentSuggestion = z.infer<typeof SentSuggestionSchema>;
export type SuggestPriceResponse = z.infer<typeof SuggestPriceResponseSchema>;
export type SuggestPriceListingData = z.infer<typeof SuggestPriceListingDataSchema>;
export type SuggestPriceCategory = z.infer<typeof SuggestPriceCategorySchema>;
export type SuggestPriceMetadata = z.infer<typeof SuggestPriceMetadataSchema>;
export type AiPriceAnalysis = z.infer<typeof AiPriceAnalysisSchema>;
export type AiFullAnalysis = z.infer<typeof AiFullAnalysisSchema>;
